import type { UnitStatReportViewModel } from 'src/api/types/UnitStatReportViewModel';
import type { UnitStatReportLossViewModel } from 'src/api/types/UnitStatReportLossViewModel';

import { varAlpha } from 'minimal-shared/utils';
import { useBoolean } from 'minimal-shared/hooks';

import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  Grid,
  Chip,
  Table,
  Tooltip,
  Divider,
  TableRow,
  Collapse,
  useTheme,
  TableHead,
  TableCell,
  TableBody,
  Typography,
  IconButton,
  TableContainer,
} from '@mui/material';

import { UnitStatReportClaimStatusEnum } from 'src/api/types/UnitStatReportClaimStatusEnum';

import { TableNoData } from 'src/components/table';

import { InfoBox } from 'src/sections/employer-data/shared/components/info-box';
import { StyledTooltipLabel } from 'src/sections/employer-data/shared/styles/sharedStyles';
import {
  formatDate,
  formatDollarAmount,
} from 'src/sections/employer-data/shared/utils/policyUtils';

type UnitStatLossesProps = {
  data: UnitStatReportViewModel | undefined;
};

export default function UnitStatLosses({ data }: UnitStatLossesProps) {
  const [unitTotals] = data?.unitStatReportUnitTotals ?? [];

  return (
    <Box sx={{ paddingTop: 2 }}>
      <Typography variant="subtitle1" sx={{ color: 'text.primary' }} data-testid="usr-losses-title">
        Loss information
      </Typography>
      <UnitStatReportLossesTable
        columns={[
          'Claim number',
          'Update type',
          'Accident date',
          'Number of claims',
          'Incurred imdemnity',
          'Incurred medical',
          'Class code',
          'Injury type',
          'Claim status',
        ]}
        rows={data?.unitStatReportLosses ?? []}
      />

      <Divider
        sx={{
          borderStyle: 'dashed',
          paddingTop: '2.5rem',
        }}
      />

      <Typography
        variant="subtitle1"
        sx={{ color: 'text.primary', mt: '2.5rem' }}
        data-testid="usr-losses-title"
      >
        Loss totals
      </Typography>
      <Grid container spacing={2} columns={4}>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Number of claims"
            value={unitTotals?.claimCountTotal || '0'}
            testIdLabel="usr-num-claims-label"
            testIdValue="usr-num-claims-value"
          />
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Incurred indemnity"
            value={formatDollarAmount(unitTotals?.incurredIndemnityTotal)}
            testIdLabel="usr-incurred-indemnity-label"
            testIdValue="usr-incurred-indemnity-value"
          />
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Incurred medical"
            value={formatDollarAmount(unitTotals?.incurredMedicalTotal)}
            testIdLabel="usr-incurred-medical-label"
            testIdValue="usr-incurred-medical-value"
          />
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Paid indemnity"
            value={formatDollarAmount(unitTotals?.paidIndemnityTotal)}
            testIdLabel="usr-paid-indemnity-label"
            testIdValue="usr-paid-indemnity-value"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} columns={4}>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Paid medical"
            value={formatDollarAmount(unitTotals?.paidMedicalTotal)}
            testIdLabel="usr-paid-medical-label"
            testIdValue="usr-paid-medical-value"
          />
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Employer attorney fees"
            value={formatDollarAmount(unitTotals?.incurredEmployersAttorneyFeesTotal)}
            testIdLabel="usr-incurred-employers-attorney-fees-label"
            testIdValue="usr-incurred-employers-attorney-fees-value"
          />
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Allocated Loss Adjustment Expenses paid"
            value={formatDollarAmount(unitTotals?.paidAllocatedLossAdjustmentExpenseTotal)}
            testIdLabel="usr-paid-allocated-loss-adjustment-expense-label"
            testIdValue="usr-paid-allocated-loss-adjustment-expense-value"
          />
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Allocated Loss Adjustment Expenses incurred"
            value={formatDollarAmount(unitTotals?.incurredAllocatedLossAdjustmentExpenseTotal)}
            testIdLabel="usr-incurred-allocated-loss-adjustment-expense-label"
            testIdValue="usr-incurred-allocated-loss-adjustment-expense-value"
          />
        </Grid>
      </Grid>
    </Box>
  );
}

function UnitStatReportLossesTable({
  rows,
  columns,
}: {
  rows: UnitStatReportLossViewModel[];
  columns: string[];
}) {
  return (
    <Box>
      <TableContainer sx={{ mt: 3, borderBottom: '1px dashed var(--palette-divider)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              {columns.map((col, index) => (
                <TableCell key={index} sx={{ width: `${100 / columns.length}%` }}>
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <Row key={rowIndex} row={row} rowIndex={rowIndex} />
            ))}
            <TableNoData
              notFound={!rows?.length}
              description="This table will be updated if the policy has any Unit Stat Report Losses to report."
              data-testid="usr-losses-no-data"
            />
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

type RowProps = {
  row: UnitStatReportLossViewModel;
  rowIndex: number;
};

function Row({ row, rowIndex }: RowProps) {
  const open = useBoolean();
  const theme = useTheme();

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={open.onToggle}>
            {open.value ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell data-testid={`claim-number-${rowIndex}`}>{row.claimNumber}</TableCell>
        <TableCell data-testid={`update-type-${rowIndex}`}>
          {row.updateTypeCodeNavigation?.updateTypeDescription}
        </TableCell>
        <TableCell data-testid={`accident-date-${rowIndex}`}>
          {formatDate(row.accidentDate)}
        </TableCell>
        <TableCell data-testid={`claim-count-${rowIndex}`}>{row.claimCount}</TableCell>

        <TableCell data-testid={`incurred-indemnity-${rowIndex}`}>
          {formatDollarAmount(row.incurredIndemnity)}
        </TableCell>

        <TableCell data-testid={`incurred-medical-${rowIndex}`}>
          {formatDollarAmount(row.incurredMedical)}
        </TableCell>

        <TableCell data-testid={`class-code-${rowIndex}`}>
          <Tooltip arrow title={row.classCodeNavigation?.description} placement="top">
            <StyledTooltipLabel
              data-testid="tool-tip-class-code-label"
              color="primary"
              sx={{
                display: 'inline-block',
              }}
            >
              <Typography component="span">{row.classCode || ''}</Typography>
            </StyledTooltipLabel>
          </Tooltip>
        </TableCell>

        <TableCell data-testid={`injury-type-${rowIndex}`}>
          {row.injuryTypeCodeNavigation?.injuryTypeDescription}
        </TableCell>

        <TableCell data-testid={`claim-status-${rowIndex}`}>
          <Chip
            label={row.claimStatusCodeNavigation?.claimStatusDescription}
            color={(() => {
              switch (row.claimStatusCodeNavigation?.claimStatusEnum) {
                case UnitStatReportClaimStatusEnum.OPEN:
                case UnitStatReportClaimStatusEnum.REOPENED:
                  return 'info';
                case UnitStatReportClaimStatusEnum.CLOSED:
                  return 'default';
                default:
                  return 'warning';
              }
            })()}
            variant="soft"
            size="small"
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          sx={{
            backgroundColor: open.value
              ? varAlpha(theme.vars.palette.grey['500Channel'], 0.4)
              : 'inherit',
            padding: open.value ? '1rem' : '0rem',
          }}
          colSpan={10}
        >
          <Collapse in={open.value} timeout="auto" unmountOnExit>
            <Box
              sx={{
                backgroundColor: 'background.paper',
                borderRadius: '1rem',
                p: '0rem 1rem 3rem 1rem',
              }}
            >
              <Grid container spacing={2} columns={4}>
                <Grid size={{ xs: 4, md: 1 }}>
                  <InfoBox
                    label="Loss act"
                    value={row.lossCoverageActCodeNavigation?.lossCoverageActDescription || ''}
                    testIdLabel="loss-act-label"
                    testIdValue="loss-act-value"
                  />
                </Grid>
                <Grid size={{ xs: 4, md: 1 }}>
                  <InfoBox
                    label="Loss type"
                    value={row.lossTypeCodeNavigation?.lossTypeDescription || ''}
                    testIdLabel="loss-type-label"
                    testIdValue="loss-type-value"
                  />
                </Grid>
                <Grid size={{ xs: 4, md: 1 }}>
                  <InfoBox
                    label="Recovery"
                    value={row.recoveryTypeCodeNavigation?.recoveryTypeDescription || ''}
                    testIdLabel="recovery-label"
                    testIdValue="recovery-value"
                  />
                </Grid>
                <Grid size={{ xs: 4, md: 1 }}>
                  <InfoBox
                    label="Claim type"
                    value={row.claimTypeCodeNavigation?.claimTypeDescription || ''}
                    testIdLabel="claim-type-label"
                    testIdValue="claim-type-value"
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} columns={4}>
                <Grid size={{ xs: 4, md: 1 }}>
                  <InfoBox
                    label="Settlement"
                    value={row.settlementTypeCodeNavigation?.settlementTypeDescription || ''}
                    testIdLabel="settlement-label"
                    testIdValue="settlement-value"
                  />
                </Grid>
                <Grid size={{ xs: 4, md: 1 }}>
                  <InfoBox
                    label="Catastrophe code"
                    value={row.catastropheNumber || ''}
                    testIdLabel="catastrophe-code-label"
                    testIdValue="catastrophe-code-value"
                  />
                </Grid>
                <Grid size={{ xs: 4, md: 1 }}>
                  <InfoBox
                    label="MCO Type"
                    value={
                      row.managedCareOrganizationTypeCodeNavigation
                        ?.managedCareOrganizationTypeDescription || ''
                    }
                    testIdLabel="mco-type-label"
                    testIdValue="mco-type-value"
                  />
                </Grid>
                <Grid size={{ xs: 4, md: 1 }}>
                  <InfoBox
                    label="Injury part"
                    value={row.bodyPartCodeNavigation?.bodyPartDescription || ''}
                    testIdLabel="body-part-label"
                    testIdValue="body-part-value"
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} columns={4}>
                <Grid size={{ xs: 4, md: 1 }}>
                  <InfoBox
                    label="Injury nature"
                    value={row.injuryNatureCodeNavigation?.injuryNatureDescription || ''}
                    testIdLabel="injury-nature-label"
                    testIdValue="injury-nature-value"
                  />
                </Grid>
                <Grid size={{ xs: 4, md: 1 }}>
                  <InfoBox
                    label="Injury cause"
                    value={row.injuryCauseCodeNavigation?.injuryCauseDescription || ''}
                    testIdLabel="injury-cause-label"
                    testIdValue="injury-cause-value"
                  />
                </Grid>
                <Grid size={{ xs: 4, md: 1 }}>
                  <InfoBox
                    label="Occupation description"
                    value={row.occupation || ''}
                    testIdLabel="occupation-label"
                    testIdValue="occupation-value"
                  />
                </Grid>
                <Grid size={{ xs: 4, md: 1 }}>
                  <InfoBox
                    label="Lump"
                    value={row.lumpSumIndicator ? 'Yes' : 'No'}
                    testIdLabel="lump-label"
                    testIdValue="lump-value"
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} columns={4}>
                <Grid size={{ xs: 4, md: 1 }}>
                  <InfoBox
                    label="Fraud claim indicator"
                    value={row.fraudulentClaimCodeNavigation?.fraudulentClaimDescription || ''}
                    testIdLabel="fraud-label"
                    testIdValue="fraud-value"
                  />
                </Grid>
                <Grid size={{ xs: 4, md: 1 }}>
                  <InfoBox
                    label="Deductible indicator"
                    value={row.deductibleReimbursement || ''}
                    testIdLabel="deductible-label"
                    testIdValue="deductible-value"
                  />
                </Grid>
                <Grid size={{ xs: 4, md: 1 }}>
                  <InfoBox
                    label="Paid indemnity"
                    value={formatDollarAmount(row.paidIndemnity)}
                    testIdLabel="paid-indemnity-label"
                    testIdValue="paid-indemnity-value"
                  />
                </Grid>
                <Grid size={{ xs: 4, md: 1 }}>
                  <InfoBox
                    label="Paid medical"
                    value={formatDollarAmount(row.paidMedical)}
                    testIdLabel="paid-medical-label"
                    testIdValue="paid-medical-value"
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} columns={4}>
                <Grid size={{ xs: 4, md: 1 }}>
                  <InfoBox
                    label="Claimant attorney fees"
                    value={formatDollarAmount(row.incurredClaimantsAttorneyFees)}
                    testIdLabel="claimant-attorney-fees-label"
                    testIdValue="claimant-attorney-fees-value"
                  />
                </Grid>
                <Grid size={{ xs: 4, md: 1 }}>
                  <InfoBox
                    label="Employer attorney fees"
                    value={formatDollarAmount(row.incurredEmployersAttorneyFees)}
                    testIdLabel="employer-attorney-fees-label"
                    testIdValue="employer-attorney-fees-value"
                  />
                </Grid>
                <Grid size={{ xs: 4, md: 1 }}>
                  <InfoBox
                    label="Allocated Loss Adjustment Expenses paid"
                    value={formatDollarAmount(row.paidAllocatedLossAdjustmentExpense)}
                    testIdLabel="allocated-loss-adjustment-expenses-paid-label"
                    testIdValue="allocated-loss-adjustment-expenses-paid-value"
                  />
                </Grid>
                <Grid size={{ xs: 4, md: 1 }}>
                  <InfoBox
                    label="Allocated Loss Adjustment Expenses incurred"
                    value={formatDollarAmount(row.incurredAllocatedLossAdjustmentExpense)}
                    testIdLabel="allocated-loss-adjustment-expenses-incurred-label"
                    testIdValue="allocated-loss-adjustment-expenses-incurred-value"
                  />
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
